export interface IConfiguracionLogistica {
  Id: string;
  Producto: string;
  Maquina: string;
  Operador: string;
  Paradas: number | string;
  Cantidades: number[];
}

/** Filtros de la tabla de Logística. MaquinaId 0 = filas sin máquina. */
export interface IFiltroLogistica {
  ProductoId?: number;
  MaquinaId?: number;
}

export interface IConfiguracionMoiAscensor {
  Id: string;
  Paradas: number | string;
  Capacidades: Record<string, number | string>;
}

export interface IConfiguracionMoiEscalera {
  Id: string;
  Altura: number | string;
  Costo: number;
  Tiempo: number;
}

export interface IConfiguracionVariacionMensual {
  Id: string;
  Mes: number | string;
  Porcentaje: number;
}

export interface IConfiguracionComisiones {
  Id: string;
  Margen: number;
  Comision: number;
}



export interface IConfiguracionState {
  Trm: number;
  MargenImportadoAscensor: number;
  MargenImportadoEscalera: number;
  MargenLocalAscensor: number;
  MargenLocalEscalera: number;
  DescuentoAscensor: number;
  DescuentoEscalera: number;
  FleteMaritimoAscensor: number;
  FleteMaritimoEscalera: number;
  GastosPuertoFob: number;
  SiaBase: number;
  MontacargasPuerto: number;
  FleteNacionalAscensor: number;
  FleteNacionalEscalera: number;
  TarifaHoraHombre: number;
  HorasDiaInstalacion: number;
  CantidadTecnicos: number;
  ValorViaticoDiaPersona: number;
  CostoLocalValorTransportePorDia: number;
  ValorPisoCiego: number;
  ValorDobleEntrada: number;
  MesesMantenimientoGratis: number;
  MantenimientoAscensorMensual: number;
  MantenimientoEscaleraMensual: number;
  BateriasEbd: number;
  Desembalaje: number;
  MontacargasInterno: number;
  InsumosInstalacion: number;
  CertificacionNtc: number;
  TransformadorKw: number;
  PorcentajeSia: number;
  PorcentajeSeguro: number;
  PorcentajeFic: number;
  PorcentajeCif: number;
  PorcentajeUpme: number;
  PorcentajeBaseGravableUpme: number;
  PorcentajePolizas: number;
  PorcentajeComisionImportacion: number;
  Imprevistos: number;
  Herramientas: number;
  ValorPromedioVuelo: number;
  /** Topes de margen y descuento de las cotizaciones (%), los define el administrador. null = sin tope. */
  MargenMinimo: number | null;
  MargenMaximo: number | null;
  DescuentoMinimo: number | null;
  DescuentoMaximo: number | null;
}

export const EstadoInicialConfiguracion: IConfiguracionState = {
  Trm: 0,
  MargenImportadoAscensor: 0,
  MargenImportadoEscalera: 0,
  MargenLocalAscensor: 0,
  MargenLocalEscalera: 0,
  DescuentoAscensor: 0,
  DescuentoEscalera: 0,
  FleteMaritimoAscensor: 0,
  FleteMaritimoEscalera: 0,
  GastosPuertoFob: 0,
  SiaBase: 0,
  MontacargasPuerto: 0,
  FleteNacionalAscensor: 0,
  FleteNacionalEscalera: 0,
  TarifaHoraHombre: 0,
  HorasDiaInstalacion: 0,
  CantidadTecnicos: 0,
  ValorViaticoDiaPersona: 0,
  CostoLocalValorTransportePorDia: 0,
  ValorPisoCiego: 0,
  ValorDobleEntrada: 0,
  MesesMantenimientoGratis: 0,
  MantenimientoAscensorMensual: 0,
  MantenimientoEscaleraMensual: 0,
  BateriasEbd: 0,
  Desembalaje: 0,
  MontacargasInterno: 0,
  InsumosInstalacion: 0,
  CertificacionNtc: 0,
  TransformadorKw: 0,
  PorcentajeSia: 0,
  PorcentajeSeguro: 0,
  PorcentajeFic: 0,
  PorcentajeCif: 0,
  PorcentajeUpme: 0,
  PorcentajeBaseGravableUpme: 0,
  PorcentajePolizas: 0,
  PorcentajeComisionImportacion: 0,
  Imprevistos: 0,
  Herramientas: 0,
  ValorPromedioVuelo: 0,
  MargenMinimo: null,
  MargenMaximo: null,
  DescuentoMinimo: null,
  DescuentoMaximo: null,
};

const VARIABLES_OBLIGATORIAS: (keyof IConfiguracionState)[] = [
  'MargenImportadoAscensor', 'MargenImportadoEscalera',
  'MargenLocalAscensor', 'MargenLocalEscalera',
  'DescuentoAscensor', 'DescuentoEscalera',
];

/** Márgenes, descuentos y (solo en países que la usan) la TRM son obligatorios para guardar la configuración (global o de una cotización). */
export const variablesObligatoriasCompletas = (config: IConfiguracionState, usaTrm: boolean) =>
  [...VARIABLES_OBLIGATORIAS, ...(usaTrm ? ['Trm'] as const : [])]
    .every(campo => config[campo] !== null && config[campo] !== undefined && String(config[campo]) !== '');

export const LogisticaInicial: IConfiguracionLogistica[] = [];

/** Matrices con carga masiva: el valor es el segmento de ruta del back (api/matrices/{matriz}/...). */
export type MatrizCargaMasiva = 'logistica' | 'moi-ascensor' | 'moi-escalera' | 'variacion-mensual' | 'comision';

/** Resultado de la carga masiva de una matriz (api/matrices/{matriz}/carga-masiva). */
export interface IResultadoCargaMasiva {
  FilasCreadas: number;
  FilasActualizadas: number;
  FilasRechazadas: number;
  /** Filas que no venían en el archivo y se borran (solo al reemplazar). */
  FilasEliminadas: number;
  /** false si no se guardó nada (simulación, o reemplazar con filas rechazadas). */
  Aplicada: boolean;
  Rechazadas: IFilaRechazadaCargaMasiva[];
}

export interface IFilaRechazadaCargaMasiva {
  Fila: number;
  /** Identificación de la fila tal como venía en el archivo (combinación, paradas, altura, mes o margen). */
  Referencia?: string;
  Errores: string[];
}

export const MoiAscensoresInicial: IConfiguracionMoiAscensor[] = [];

export const MoiEscalerasInicial: IConfiguracionMoiEscalera[] = [];

export const VariacionMensualInicial: IConfiguracionVariacionMensual[] = [
  { Id: '1', Mes: 'Enero', Porcentaje: 0 },
  { Id: '2', Mes: 'Febrero', Porcentaje: 0 },
  { Id: '3', Mes: 'Marzo', Porcentaje: 0 },
  { Id: '4', Mes: 'Abril', Porcentaje: 0 },
  { Id: '5', Mes: 'Mayo', Porcentaje: 0 },
  { Id: '6', Mes: 'Junio', Porcentaje: 0 },
  { Id: '7', Mes: 'Julio', Porcentaje: 0 },
  { Id: '8', Mes: 'Agosto', Porcentaje: 0 },
  { Id: '9', Mes: 'Septiembre', Porcentaje: 0 },
  { Id: '10', Mes: 'Octubre', Porcentaje: 0 },
  { Id: '11', Mes: 'Noviembre', Porcentaje: 0 },
  { Id: '12', Mes: 'Diciembre', Porcentaje: 0 },
];

